import { useCallback, useEffect, useRef, useState } from "react";

import { squareAvatar } from "~/lib/avatar-image";
import { getUserProfile, setOwnAvatar, uploadContent, type Client } from "~/lib/matrix";

export interface OwnAvatar {
  /** The current `mxc://` photo, or null when there is none. */
  mxc: string | null;
  busy: boolean;
  failed: boolean;
  choose: (file: File) => Promise<void>;
  remove: () => Promise<void>;
}

/** The signed-in user's profile photo, with change and remove. */
export function useOwnAvatar(client: Client | null, userId: string | null): OwnAvatar {
  const [mxc, setMxc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // Bumped on account change and on every change the user makes, so a
  // late profile read or a previous account's upload never lands.
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    const mine = generation.current;
    setMxc(null);
    setBusy(false);
    setFailed(false);
    if (!client || !userId) return;
    getUserProfile(client, userId)
      .then((profile) => {
        if (generation.current !== mine) return;
        setMxc(profile.avatarUrl?.startsWith("mxc://") ? profile.avatarUrl : null);
      })
      .catch(() => undefined);
  }, [client, userId]);

  const run = useCallback(
    async (next: (client: Client) => Promise<string | null>) => {
      if (!client) return;
      generation.current += 1;
      const mine = generation.current;
      setBusy(true);
      setFailed(false);
      try {
        const url = await next(client);
        await setOwnAvatar(client, url);
        if (generation.current === mine) setMxc(url);
      } catch (err) {
        console.warn("profile photo update failed", err);
        if (generation.current === mine) setFailed(true);
      } finally {
        if (generation.current === mine) setBusy(false);
      }
    },
    [client],
  );

  const choose = useCallback(
    (file: File) =>
      run(async (c) => {
        const { mxc: url } = await uploadContent(c, await squareAvatar(file));
        return url;
      }),
    [run],
  );

  const remove = useCallback(() => run(async () => null), [run]);

  return { mxc, busy, failed, choose, remove };
}
